import { useMemo, useState } from 'react'
import * as stylex from '@stylexjs/stylex'

import Avatar from '../../components/Avatar'
import Button from '../../components/Button'
import Card from '../../components/Card'
import EmptyState from '../../components/EmptyState'
import Icon from '../../components/Icon'
import PageHeader from '../../components/PageHeader'
import SearchInput from '../../components/SearchInput'
import SegmentedControl from '../../components/SegmentedControl'
import { formatMoney, type Balance, type Overview, type Person } from '../../lib/ledger'
import { mobileStyles as styles } from '../mobileStyles'

type PeopleMobilePageProps = {
    data: Overview
    onAddPerson: () => void
    onSettle: (balance: Balance) => void
    onShare: (personID: string) => void
    onOpenPerson: (personID: string) => void
}

type Filter = 'all' | 'owed' | 'owes' | 'settled'

const filters = [
    { label: 'All', value: 'all' },
    { label: 'Owed to you', value: 'owed' },
    { label: 'You owe', value: 'owes' },
    { label: 'Settled', value: 'settled' },
] as const

const localStyles = stylex.create({
    toolbar: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    resultMeta: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '10px',
        paddingInline: '2px',
        color: '#748077',
        fontSize: '11px',
    },
    resultStrong: {
        color: '#425348',
        fontWeight: 650,
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
    },
    person: {
        padding: '14px',
        borderBottomWidth: 1,
        borderBottomColor: '#edf0eb',
        ':last-child': {
            borderBottomWidth: 0,
        },
    },
    personButton: {
        display: 'grid',
        width: '100%',
        gridTemplateColumns: '48px minmax(0, 1fr) auto',
        alignItems: 'center',
        gap: '11px',
        borderRadius: '0.75rem',
        textAlign: 'left',
        WebkitTapHighlightColor: 'transparent',
        ':active': {
            opacity: 0.72,
        },
    },
    personCopy: {
        minWidth: 0,
    },
    personName: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        color: '#2c362f',
        fontSize: '14px',
        fontWeight: 650,
    },
    personContact: {
        display: 'block',
        overflow: 'hidden',
        marginTop: '3px',
        color: '#7a857c',
        fontSize: '11px',
        lineHeight: '1rem',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    chevron: {
        color: '#91a096',
    },
    balances: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        marginTop: '12px',
        paddingTop: '12px',
        borderTopWidth: 1,
        borderTopColor: '#f0f3ee',
    },
    balanceRow: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto auto',
        alignItems: 'center',
        gap: '9px',
        minHeight: '36px',
        '@media (max-width: 360px)': {
            gridTemplateColumns: 'minmax(0, 1fr) auto',
        },
    },
    balanceKind: {
        minWidth: 0,
        color: '#748077',
        fontSize: '11px',
        fontWeight: 550,
    },
    amount: {
        whiteSpace: 'nowrap',
        fontSize: '13px',
        fontWeight: 700,
        letterSpacing: '-.01em',
    },
    record: {
        '@media (max-width: 360px)': {
            gridColumn: '1 / -1',
            width: '100%',
        },
    },
    settled: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        alignSelf: 'flex-start',
        borderRadius: '9999px',
        backgroundColor: '#edf6eb',
        paddingInline: '10px',
        paddingBlock: '6px',
        color: '#397a4b',
        fontSize: '11px',
        fontWeight: 650,
    },
    emptyWrap: {
        padding: '8px',
    },
})

function personMatches(person: Person, query: string) {
    if (!query) return true
    const searchable = [person.name, person.email, person.phone]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
    return searchable.includes(query)
}

export default function PeopleMobilePage({ data, onAddPerson, onSettle, onShare, onOpenPerson }: PeopleMobilePageProps) {
    const [filter, setFilter] = useState<Filter>('all')
    const [query, setQuery] = useState('')

    const balancesByPerson = useMemo(() => {
        const map = new Map<string, Balance[]>()
        for (const balance of data.combinedBalances ?? data.balances) {
            const balances = map.get(balance.personId) ?? []
            balances.push(balance)
            map.set(balance.personId, balances)
        }
        for (const balances of map.values()) {
            balances.sort((left, right) => left.currency.localeCompare(right.currency))
        }
        return map
    }, [data.balances, data.combinedBalances])

    const normalizedQuery = query.trim().toLowerCase()
    const people = useMemo(() => {
        return [...data.people]
            .sort((left, right) => {
                const leftBalances = balancesByPerson.get(left.id) ?? []
                const rightBalances = balancesByPerson.get(right.id) ?? []
                const leftActive = leftBalances.some((balance) => balance.amountCents !== 0) ? 1 : 0
                const rightActive = rightBalances.some((balance) => balance.amountCents !== 0) ? 1 : 0
                return rightActive - leftActive || left.name.localeCompare(right.name)
            })
            .filter((person) => {
                const balances = balancesByPerson.get(person.id) ?? []
                const matchesFilter = filter === 'owed'
                    ? balances.some((balance) => balance.amountCents > 0)
                    : filter === 'owes'
                        ? balances.some((balance) => balance.amountCents < 0)
                        : filter === 'settled'
                            ? balances.length === 0 || balances.every((balance) => balance.amountCents === 0)
                            : true
                return matchesFilter && personMatches(person, normalizedQuery)
            })
    }, [balancesByPerson, data.people, filter, normalizedQuery])

    const emptyDescription = normalizedQuery
        ? `No people match “${query.trim()}”.`
        : filter === 'owed'
            ? 'No one currently owes you.'
            : filter === 'owes'
                ? 'You do not currently owe anyone.'
                : filter === 'settled'
                    ? 'No settled people yet.'
                    : 'Add someone to start tracking shared expenses.'

    return (
        <div {...stylex.props(styles.page)}>
            <PageHeader
                action={<Button aria-label="Add person" onClick={onAddPerson} size="sm"><Icon name="plus" />Add</Button>}
                eyebrow="Your circle"
                subtitle="Search, review balances, and record payments."
                title="People"
            />

            <div {...stylex.props(localStyles.toolbar)}>
                <SearchInput
                    aria-label="Search people"
                    autoComplete="off"
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search name, email, or phone…"
                    value={query}
                />
                <SegmentedControl columns={2} onChange={setFilter} options={filters} value={filter} />
                <div {...stylex.props(localStyles.resultMeta)} aria-live="polite">
                    <span><strong {...stylex.props(localStyles.resultStrong)}>{people.length}</strong> {people.length === 1 ? 'person' : 'people'}</span>
                    {(query || filter !== 'all') && <button onClick={() => { setQuery(''); setFilter('all') }} type="button">Clear filters</button>}
                </div>
            </div>

            <Card padding="none">
                {people.length === 0 ? (
                    <div {...stylex.props(localStyles.emptyWrap)}>
                        <EmptyState
                            action={data.people.length === 0 ? <Button onClick={onAddPerson}>Add person</Button> : undefined}
                            description={emptyDescription}
                            icon={<Icon name="people" />}
                        />
                    </div>
                ) : (
                    <div {...stylex.props(localStyles.list)}>
                        {people.map((person) => {
                            const balances = balancesByPerson.get(person.id) ?? []
                            const activeBalances = balances.filter((balance) => balance.amountCents !== 0)
                            const contact = person.email || person.phone || (activeBalances.length > 0 ? `${activeBalances.length} active ${activeBalances.length === 1 ? 'balance' : 'balances'}` : 'All settled up')

                            return (
                                <article {...stylex.props(localStyles.person)} key={person.id}>
                                    <button {...stylex.props(localStyles.personButton)} onClick={() => onOpenPerson(person.id)} type="button">
                                        <Avatar person={person} size={48} />
                                        <span {...stylex.props(localStyles.personCopy)}>
                                            <span {...stylex.props(localStyles.personName)}>{person.name}</span>
                                            <span {...stylex.props(localStyles.personContact)}>{contact}</span>
                                        </span>
                                        <Icon {...stylex.props(localStyles.chevron)} name="arrow-right" />
                                    </button>

                                    <div {...stylex.props(localStyles.balances)}>
                                        {activeBalances.length === 0 ? (
                                            <span {...stylex.props(localStyles.settled)}><Icon name="check" />Settled up</span>
                                        ) : activeBalances.map((balance) => {
                                            const receives = balance.amountCents > 0
                                            return (
                                                <div {...stylex.props(localStyles.balanceRow)} key={`${person.id}-${balance.currency}`}>
                                                    <span {...stylex.props(localStyles.balanceKind)}>{receives ? 'Owes you' : 'You owe'} · {balance.currency}</span>
                                                    <span {...stylex.props(localStyles.amount, receives ? styles.positive : styles.negative)}>{formatMoney(Math.abs(balance.amountCents), balance.currency)}</span>
                                                    <Button
                                                        {...stylex.props(localStyles.record)}
                                                        aria-label={`Record ${balance.currency} payment with ${person.name}`}
                                                        onClick={() => data.combinedBalances ? onOpenPerson(person.id) : onSettle(balance)}
                                                        size="sm"
                                                        variant="secondary"
                                                    >
                                                        {data.combinedBalances ? 'Details' : 'Record'}
                                                    </Button>
                                                </div>
                                            )
                                        })}
                                        <Button onClick={() => onShare(person.id)} size="sm" variant="secondary">Share overview</Button>
                                    </div>
                                </article>
                            )
                        })}
                    </div>
                )}
            </Card>
        </div>
    )
}
